import { useState } from 'react'
import DataTable from '../../components/DataTable'
import StatsCard from '../../components/StatsCard'
import { mockVetClinics } from '../../data/mockData'
import { Star, Stethoscope, MapPin, PhoneCall } from 'lucide-react'

export default function VetClinicsPage() {
  const [sortKey, setSortKey] = useState('rating')
  const [sortOrder, setSortOrder] = useState('desc')
  const [filterStatus, setFilterStatus] = useState('All')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  // Filter clinics
  let filteredClinics = mockVetClinics
  if (filterStatus !== 'All') {
    filteredClinics = filteredClinics.filter((c) => c.status === filterStatus)
  }

  const totalReviews = mockVetClinics.reduce((sum, c) => sum + c.reviews, 0)
  const avgRating = (mockVetClinics.reduce((sum, c) => sum + c.rating, 0) / mockVetClinics.length).toFixed(1)
  const activeClinics = mockVetClinics.filter((c) => c.status === 'Active').length

  const columns = [
    { key: 'name', label: 'Clinic Name', sortable: true },
    { key: 'location', label: 'Location', sortable: false },
    {
      key: 'rating',
      label: 'Rating',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={14} className={i < Math.floor(value) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} />
          ))}
          <span className="text-sm font-medium ml-2">{value}</span>
        </div>
      ),
    },
    {
      key: 'reviews',
      label: 'Reviews',
      sortable: false,
      render: (value) => `${value} reviews`,
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          value === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
        }`}>{value}</span>
      ),
    },
    {
      key: 'phone',
      label: 'Contact',
      render: (value) => (
        <div className="flex items-center gap-2">
          <PhoneCall size={14} className="text-pawssible-slate-400" />
          {value}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Vet Clinics</h1>
        <p className="text-pawssible-slate-600 mt-1">Partner veterinary clinics & booking management</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          label="Total Clinics"
          value={mockVetClinics.length}
          icon={Stethoscope}
          color="teal"
        />
        <StatsCard
          label="Active Clinics"
          value={activeClinics}
          icon={MapPin}
          color="amber"
        />
        <StatsCard
          label="Average Rating"
          value={avgRating}
          icon={Star}
          color="teal"
        />
        <StatsCard
          label="Total Reviews"
          value={totalReviews}
          icon={PhoneCall}
          color="amber"
        />
      </div>

      {/* Clinic Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {mockVetClinics.map((clinic) => (
          <div key={clinic.id} className="card">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="text-lg font-semibold text-pawssible-slate-900">{clinic.name}</h4>
                <div className="flex items-center gap-1 mt-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      className={i < Math.floor(clinic.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                    />
                  ))}
                  <span className="text-xs font-medium text-pawssible-slate-600 ml-1">{clinic.rating} ({clinic.reviews})</span>
                </div>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                clinic.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {clinic.status}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-pawssible-slate-600">
                <MapPin size={14} />
                {clinic.location}
              </div>
              <div className="flex items-center gap-2 text-pawssible-slate-600">
                <PhoneCall size={14} />
                {clinic.phone}
              </div>
              <div className="pt-2 border-t border-pawssible-slate-100">
                <p className="text-xs text-pawssible-slate-600 mb-2">Services:</p>
                <div className="flex flex-wrap gap-1">
                  {clinic.services.map((service, i) => (
                    <span key={i} className="bg-pawssible-teal-50 text-pawssible-teal-700 px-2 py-1 rounded text-xs">
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Controls */}
      <div className="card">
        <label className="label">Filter by Status</label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="input"
        >
          <option>All</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 flex-wrap">
        <button className="btn-primary">Add Clinic</button>
        <button className="btn-secondary">Export List</button>
        <button className="btn-secondary">View Bookings</button>
      </div>

      {/* Clinics Table */}
      <div>
        <h2 className="text-xl font-semibold text-pawssible-slate-900 mb-4">All Clinics</h2>
        <DataTable
          columns={columns}
          data={filteredClinics}
          onSort={handleSort}
          sortKey={sortKey}
          sortOrder={sortOrder}
          pageSize={15}
        />
      </div>
    </div>
  )
}
